import { TeamType } from '@modules/Auth/models/Auth';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useAuthStore } from '@shared/stores/authStore';
import React from 'react';

interface PermissionGuardProps {
  children: React.ReactNode;
  teamTypes?: TeamType[];
  permissions?: string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
}

/**
 * Componente que renderiza children solo si el usuario cumple los requisitos de acceso
 *
 * @param teamTypes - Array de tipos de team permitidos (opcional)
 * @param permissions - Array de permisos requeridos (opcional)
 * @param requireAll - Si true, requiere todos los permisos; si false, solo uno (default: false)
 * @param fallback - Elemento a renderizar si no tiene acceso (default: null)
 *
 * @example
 * ```tsx
 * <PermissionGuard
 *   teamTypes={['tenant', 'tenant_3pl']}
 *   permissions={['products.create']}
 * >
 *   <CreateProductButton />
 * </PermissionGuard>
 * ```
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  teamTypes,
  permissions,
  requireAll = false,
  fallback = null,
}) => {
  const { canAccess, hasAllPermissions } = usePermissions();
  const { token, role } = useAuthStore();

  // 🎯 Si hay token pero no hay role, está cargando - mostrar fallback o null
  if (token && !role) {
    return <>{fallback}</>;
  }

  // Si requireAll es true, verificar que tenga TODOS los permisos
  // De lo contrario, verificar que tenga AL MENOS UNO
  let hasAccess = false;

  if (requireAll && permissions && permissions.length > 0) {
    // Verificar team_type Y todos los permisos
    hasAccess = canAccess(teamTypes, []) && hasAllPermissions(permissions);
  } else {
    // Verificación estándar: team_type + al menos uno de los permisos
    hasAccess = canAccess(teamTypes, permissions);
  }

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};
